import { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { compact, usd } from "@/lib/format";

const TT = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="glass px-3 py-2 text-xs space-y-1">
      <p className="font-medium">{label}</p>
      {payload.filter((p) => p.value != null && p.dataKey !== "band").map((p) => (
        <p key={p.dataKey} className="font-mono-num" style={{ color: p.color }}>{p.name}: {usd(p.value)}</p>
      ))}
    </div>
  ) : null;

export function BalanceChart({ history = [], projection = [], target, height = 320 }) {
  const data = useMemo(() => {
    const year0 = new Date().getFullYear();
    const hist = history.map((h) => ({ label: h.date, actual: h.total ?? h.tsp }));
    const proj = projection.map((p) => ({
      label: `${p.year} (age ${p.age})`, expected: p.expected, conservative: p.conservative, optimistic: p.optimistic,
      band: [p.conservative, p.optimistic], target: target ? Math.round(target * ((p.year - year0) / Math.max(1, projection.length - 1))) : null,
    }));
    return [...hist, ...proj];
  }, [history, projection, target]);
  return (
    <div style={{ height }} data-testid="balance-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06B6D4" stopOpacity={0.25} /><stop offset="100%" stopColor="#06B6D4" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-800" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94A3B8" minTickGap={40} />
          <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} stroke="#94A3B8" width={64} />
          <Tooltip content={<TT />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area dataKey="band" name="Projection cone" stroke="none" fill="#10B981" fillOpacity={0.12} legendType="square" />
          <Area dataKey="actual" name="Historical balance" stroke="#0F172A" className="dark:[&_path]:stroke-white" strokeWidth={2} fill="#0F172A" fillOpacity={0.06} connectNulls />
          <Area dataKey="expected" name="Expected" stroke="#06B6D4" strokeWidth={2.5} fill="url(#gExp)" />
          <Line dataKey="conservative" name="Conservative" stroke="#F59E0B" strokeDasharray="4 4" dot={false} />
          <Line dataKey="optimistic" name="Optimistic" stroke="#10B981" strokeDasharray="4 4" dot={false} />
          <Line dataKey="target" name="Target path" stroke="#6366F1" strokeWidth={1.5} dot={false} strokeDasharray="2 6" />
          {target ? <ReferenceLine y={target} stroke="#6366F1" strokeDasharray="6 4" label={{ value: `Goal ${compact(target)}`, position: "insideTopLeft", fontSize: 11, fill: "#6366F1" }} /> : null}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
